import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Loader2, Building2, MapPin, FileText } from "lucide-react";

export const STATUS_STYLES = {
  Submitted: "bg-slate-500",
  "Under Review": "bg-amber-500",
  Interview: "bg-blue-600",
  Offered: "bg-emerald-600",
  Rejected: "bg-red-500",
};

export default function MyApplications() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [apps, setApps] = useState(null);

  useEffect(() => {
    api.get("/my/applications").then(({ data }) => setApps(data));
  }, []);

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      <main className="mx-auto max-w-4xl px-4 sm:px-6 py-10" data-testid="my-applications">
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-slate-900 mb-2">{t("myApplications.title")}</h1>
        <p className="text-slate-500 mb-8">{t("myApplications.subtitle")}</p>

        {apps === null ? (
          <div className="flex justify-center py-20">
            <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
          </div>
        ) : apps.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-16 text-center">
            <FileText className="mx-auto h-10 w-10 text-slate-300" />
            <p className="mt-4 text-slate-500">{t("myApplications.empty")}</p>
            <Button onClick={() => navigate("/jobs")} className="mt-4 bg-blue-600 hover:bg-blue-700">
              {t("myApplications.browseJobs")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {apps.map((a) => (
              <Card key={a.application_id} data-testid="application-row" className="border-slate-200 p-5">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <h3 className="font-heading font-bold text-slate-900">{a.job?.title || t("myApplications.roleRemoved")}</h3>
                    <p className="text-sm text-slate-500 flex items-center gap-2 mt-0.5">
                      <Building2 className="h-3.5 w-3.5" /> {a.job?.company}
                      {a.job?.location && (
                        <>
                          <span className="text-slate-300">•</span>
                          <MapPin className="h-3.5 w-3.5" /> {a.job.location}
                        </>
                      )}
                    </p>
                    <p className="mt-1 text-xs text-slate-400">
                      {t("myApplications.appliedOn", { date: new Date(a.applied_at).toLocaleDateString() })}
                    </p>
                  </div>
                  <Badge className={STATUS_STYLES[a.status] || "bg-slate-500"} data-testid="application-status">
                    {a.status}
                  </Badge>
                </div>
              </Card>
            ))}
          </div>
        )}
      </main>
    </div>
  );
}
